import { postRequest } from '../../http'
import config from '../../../config'
import { FormTypes } from '@oneblink/types'
import cleanSubmissionModel from '../../clean-submission-model'

// tax rate must be a whole number, as pivotal sends them as such e.g 10 => 10%
function calculateGSTCharge(chargeIncGst: number, taxRate: number | null) {
  const taxCoefficient = 1 + (taxRate || 0) / 100
  return Math.round((chargeIncGst - chargeIncGst / taxCoefficient) * 100) / 100
}

export function round(number: number, decimals: number) {
  const multiplier = Math.pow(10, decimals)
  return Math.round(number * multiplier) / multiplier
}

async function getFacilitiesWithPrices(
  facilitiesWithPermissionsAndProcesses: Facility[],
  facilityPath: string,
  licenseNumber: string | undefined,
) {
  const facilitiesWithPrices: FacilityWithPermissionsAndProcesses[] = []

  for (const facility of facilitiesWithPermissionsAndProcesses) {
    const facilityRes = await postRequest<FacilityWithPermissionsAndProcesses>({
      url: `${config.HOSTED_API_URL}${facilityPath}`,
      resource: { facility, licenseNumber },
    })
    facilitiesWithPrices.push(facilityRes)
  }

  return facilitiesWithPrices
}

export default async function getPricing({
  submission,
  definition,
  permissionsPath,
  productsPath,
  facilityPath,
}: {
  submission: Record<string, unknown>
  definition: FormTypes.Form
  permissionsPath: string
  productsPath: string
  facilityPath: string
}) {
  const cleanedSubmission = cleanSubmissionModel(
    submission,
    definition.elements,
  )

  const facilitiesWithPermissionsAndProcesses = await postRequest<Facility[]>({
    url: `${config.HOSTED_API_URL}${permissionsPath}`,
    resource: { submission: cleanedSubmission, definition },
  })

  const facilities = await getFacilitiesWithPrices(
    facilitiesWithPermissionsAndProcesses,
    facilityPath,
    submission.License_Number as string | undefined,
  )

  const {
    products__hidden,
    paymentTotal__hidden,
    scriptId__hidden,
    companyInterestsId__hidden,
  } = await postRequest<{
    products__hidden: Product[]
    paymentTotal__hidden: number
    scriptId__hidden: number
    companyInterestsId__hidden: number
  }>({
    url: `${config.HOSTED_API_URL}${productsPath}`,
    resource: { submission: cleanedSubmission },
  })

  const { applicationFee, applicationFeeTaxRate } = facilities.reduce(
    (feeData, facility) => {
      if (!('paymentResponse' in facility)) return feeData
      if (!facility.paymentResponse?.ApplyFee_AmountIncGST) return feeData

      if (
        facility.paymentResponse?.ApplyFee_AmountIncGST > feeData.applicationFee
      )
        return {
          applicationFee: facility.paymentResponse?.ApplyFee_AmountIncGST,
          applicationFeeTaxRate: facility.paymentResponse?.ApplyFee_Tax_rate,
        }

      return feeData
    },
    {
      applicationFee: 0,
      applicationFeeTaxRate: 0,
    },
  )

  const facilitiesWithGSTAndPermission = facilities.map((facility) => {
    if (!facility.paymentResponse) {
      return facility
    }

    const permissionCost = facility.price
    const taxRate = facility.paymentResponse.Permission_Tax_rate
    const facilityApplicationFee =
      facility.paymentResponse.ApplyFee_AmountIncGST
    return {
      ...facility,
      applicationFee: facilityApplicationFee,
      applicationFeeGST: calculateGSTCharge(
        facilityApplicationFee,
        facility.paymentResponse.ApplyFee_Tax_rate,
      ),
      applicationFeePermissionId: facility.paymentResponse.Permission_Id,
      gstAmount: calculateGSTCharge(permissionCost, taxRate),
    }
  })

  const payloadWithoutTotal = {
    facilities__hidden: facilitiesWithGSTAndPermission,
    applicationFee__hidden: applicationFee,
    applicationFeeTaxRate__hidden: applicationFeeTaxRate,
    applicationFeePriorPaymentRequired__hidden: true,
    hasPricingLookupRun__hidden: 'yes',
  }

  const facilityCharges = facilities.reduce((facilitySum: number, facility) => {
    if (!('price' in facility)) return facilitySum
    return (facilitySum += facility.price || 0)
  }, 0)

  const paymentTotal = round(
    facilityCharges +
      paymentTotal__hidden +
      payloadWithoutTotal.applicationFee__hidden,
    2,
  )

  return {
    ...payloadWithoutTotal,
    paymentTotal__hidden: paymentTotal,
    products__hidden,
    companyInterestsId__hidden,
    scriptId__hidden,
  }
}
